/**
 * Manual/Handbook API Client
 *
 * API client for handbook/user guide functionality.
 * Story 7-2, Story 2-3 (search)
 */

import { apiRequest } from './api';
import type { SearchResponse } from '@functieloonhuis/shared';

export interface ManualSection {
  id: number;
  slug: string;
  title: string;
  content: string;
  visibility: 'all' | 'medewerker' | 'beoordelaar' | 'hr';
  hasDynamicData: boolean;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Story 6-2: Parent section ID for hierarchy (null = root level) */
  parentId: number | null;
  /** Story 6-2: Section type in hierarchy */
  sectionType: 'part' | 'chapter' | 'section' | 'subsection';
  /** Story 6-2: Display order within parent */
  displayOrder: number;
}

/**
 * BreadcrumbItem interface for path navigation
 * Story 1-4: ManualBreadcrumb
 */
export interface BreadcrumbItem {
  slug: string;
  title: string;
}

/**
 * Section with breadcrumb path for navigation
 * Story 1-4: ManualBreadcrumb
 */
export interface SectionWithPath extends ManualSection {
  path: BreadcrumbItem[];
}

export interface ManualVersion {
  id: number;
  versionNumber: string;
  changelog: string;
  publishedBy: number;
  publishedByName: string;
  publishedAt: string;
}

export interface ManualSectionsResponse {
  data: ManualSection[];
}

/**
 * TreeNode interface for hierarchical manual structure
 * Used for tree navigation sidebar
 */
export interface TreeNode {
  id: number;
  slug: string;
  title: string;
  sectionType: 'part' | 'chapter' | 'section' | 'subsection';
  displayOrder: number;
  visibility: 'all' | 'medewerker' | 'beoordelaar' | 'hr';
  children: TreeNode[];
}

export interface ManualTreeResponse {
  data: TreeNode[];
}

export interface VersionHistoryResponse {
  data: ManualVersion[];
}

export interface CreateSectionInput {
  slug: string;
  title: string;
  content: string;
  visibility: 'all' | 'medewerker' | 'beoordelaar' | 'hr';
  sortOrder: number;
  hasDynamicData: boolean;
  /** Story 6-2: Parent section ID for hierarchy */
  parentId?: number | null;
  /** Story 6-2: Section type in hierarchy */
  sectionType?: 'part' | 'chapter' | 'section' | 'subsection';
  /** Story 6-2: Display order within parent */
  displayOrder?: number;
}

export interface UpdateSectionInput {
  slug?: string;
  title?: string;
  content?: string;
  visibility?: 'all' | 'medewerker' | 'beoordelaar' | 'hr';
  sortOrder?: number;
  hasDynamicData?: boolean;
  isActive?: boolean;
  /** Story 6-2: Parent section ID for hierarchy */
  parentId?: number | null;
  /** Story 6-2: Section type in hierarchy */
  sectionType?: 'part' | 'chapter' | 'section' | 'subsection';
  /** Story 6-2: Display order within parent */
  displayOrder?: number;
}

export interface PublishVersionInput {
  versionNumber: string;
  changelog: string;
}

/**
 * Manual API client
 */
/**
 * Placeholder data response type
 * Story 5.2: System Data Placeholders
 */
export interface PlaceholderResponse {
  data: string;
}

export const manualApi = {
  /**
   * Fetch all manual sections for current user
   */
  async fetchManual(): Promise<ManualSection[]> {
    const response = await apiRequest<ManualSectionsResponse>('/manual');
    return response.data;
  },

  /**
   * Fetch placeholder data by type
   * Story 5.2: System Data Placeholders
   *
   * @param type - Placeholder type (tasks_list, levels_list, score_criteria, current_cycle, my_tasks, my_competencies)
   * @returns Markdown formatted placeholder data
   */
  async fetchPlaceholder(type: string): Promise<string> {
    const response = await apiRequest<PlaceholderResponse>(`/manual/placeholder/${type}`);
    return response.data;
  },

  /**
   * Fetch hierarchical tree of manual sections for tree navigation
   */
  async fetchTree(): Promise<TreeNode[]> {
    const response = await apiRequest<ManualTreeResponse>('/manual/tree');
    return response.data;
  },

  /**
   * Fetch single section by slug with breadcrumb path
   * Story 1-4: ManualBreadcrumb
   */
  async fetchSection(slug: string): Promise<SectionWithPath> {
    return apiRequest<SectionWithPath>(`/manual/sections/${slug}`);
  },

  /**
   * Fetch version history/changelog
   */
  async fetchChangelog(): Promise<ManualVersion[]> {
    const response = await apiRequest<VersionHistoryResponse>('/manual/changelog');
    return response.data;
  },

  /**
   * Search manual sections with FTS5 full-text search.
   * Story 2-3: ManualSearch Component
   *
   * @param query - Search query string (min 2 characters)
   * @param limit - Maximum results (default 20, max 50)
   * @returns Search response with results and total count
   */
  async search(query: string, limit: number = 20): Promise<SearchResponse> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    return apiRequest<SearchResponse>(`/manual/search?${params}`);
  },

  // Admin functions
  /**
   * Fetch all manual sections (admin only)
   */
  async fetchAdminSections(): Promise<ManualSection[]> {
    const response = await apiRequest<ManualSectionsResponse>('/admin/manual/sections');
    return response.data;
  },

  /**
   * Create new section (admin only)
   */
  async createSection(data: CreateSectionInput): Promise<ManualSection> {
    return apiRequest<ManualSection>('/admin/manual/sections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update section (admin only)
   */
  async updateSection(id: number, data: UpdateSectionInput): Promise<ManualSection> {
    return apiRequest<ManualSection>(`/admin/manual/sections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete section (admin only)
   */
  async deleteSection(id: number): Promise<void> {
    await apiRequest(`/admin/manual/sections/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Publish new version (admin only)
   */
  async publishVersion(data: PublishVersionInput): Promise<ManualVersion> {
    return apiRequest<ManualVersion>('/admin/manual/publish', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Move section to new parent/position (admin only)
   * Story 6-3: SectionTreeEditor Component
   */
  async moveSection(
    id: number,
    data: { parentId: number | null; displayOrder?: number }
  ): Promise<ManualSection> {
    return apiRequest<ManualSection>(`/admin/manual/sections/${id}/move`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};
